export type AnomalyType = "outside_limits" | "two_sigma" | "run_of_eight";

export interface AnomalyRange {
  type: AnomalyType;
  start: Date; // inclusive
  end: Date; // inclusive
}

type anomalyDetectionFn = (
  data: (number | null)[],
  mean: number,
  sigma: number,
  lcl: number | number[],
  ucl: number | number[],
) => Set<number>;

const rules: Record<AnomalyType, anomalyDetectionFn> = {
  // outside control limits
  outside_limits: (data, _mean, _sigma, lcl, ucl) => {
    const anomalies = new Set<number>();

    if (typeof lcl === "number" && typeof ucl === "number") {
      data.forEach((v, i) => {
        if (v != null && (v > ucl || v < lcl)) anomalies.add(i);
      });
    } else if (Array.isArray(lcl) && Array.isArray(ucl)) {
      data.forEach((v, i) => {
        if (v != null && (v > ucl[i] || v < lcl[i])) anomalies.add(i);
      });
    } else {
      throw new Error("Invalid control limits for anomaly detection");
    }

    return anomalies;
  },
  // 2 of 3 consecutive points on the same side of mean and >= 2 sigma from mean
  two_sigma: (data, mean, sigma) => {
    const twoSigma = 2 * sigma;

    const anomalies = new Set<number>();
    for (let i = 0; i <= data.length - 3; i++) {
      const windowIdx = [i, i + 1, i + 2];
      const windowVals = windowIdx.map(idx => data[idx]);
      // Skip windows with null values
      if (windowVals.some(v => v == null)) continue;
      const side = windowVals.map(v => (v! >= mean ? 1 : -1));
      const sameSide = side.every(s => s === side[0]);
      if (!sameSide) continue;
      const countTwoSigma = windowVals.filter(v => Math.abs(v! - mean) >= twoSigma).length;
      if (countTwoSigma >= 2) {
        for (const idx of windowIdx) anomalies.add(idx);
      }
    }

    return anomalies;
  },
  // run of 8 on the same side of mean
  run_of_eight: (data, mean) => {
    if (mean === undefined) {
      return new Set<number>();
    }
    const anomalies = new Set<number>();
    for (let i = 0; i <= data.length - 8; i++) {
      const windowIdx = Array.from({ length: 8 }, (_, k) => i + k);
      const windowVals = windowIdx.map(idx => data[idx]);
      // Skip windows with null values
      if (windowVals.some(v => v == null)) continue;
      const allAbove = windowVals.every(v => v! > mean);
      const allBelow = windowVals.every(v => v! < mean);
      if (allAbove || allBelow) {
        for (const idx of windowIdx) anomalies.add(idx);
      }
    }
    return anomalies;
  },
};

// Compute anomaly index sets per rules (red outside limits, orange 2 of 3 >=2σ same side, blue run of 8)
function computeAnomalyIndexSets(
  anomalyTypesSortedByPriority: AnomalyType[],
  data: (number | null)[],
  mean: number,
  sigma: number,
  lcl: number | number[],
  ucl: number | number[],
): Record<AnomalyType, Set<number>> {
  const result = {} as Record<AnomalyType, Set<number>>;

  for (let i = 0; i < anomalyTypesSortedByPriority.length; i++) {
    const type = anomalyTypesSortedByPriority[i];
    const detectionFn = rules[type];
    if (!detectionFn) {
      throw new Error(`Unknown anomaly detection type: ${type}`);
    }

    const anomalies = detectionFn(data, mean, sigma, lcl, ucl);
    // Remove indices already claimed by higher-priority rules
    for (const previousType of anomalyTypesSortedByPriority.slice(0, i)) {
      const previousAnomalies = result[previousType];
      if (previousAnomalies) {
        for (const idx of previousAnomalies) {
          anomalies.delete(idx);
        }
      }
    }
    result[type] = anomalies;
  }

  return result;
}

// Merge contiguous indices into anomaly ranges for a given type using timestamps
function mergeIndices(indices: Set<number>, type: AnomalyType, timestamps: Date[]): AnomalyRange[] {
  if (indices.size === 0) return [];
  const sorted = Array.from(indices).sort((a, b) => a - b);
  const ranges: AnomalyRange[] = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    const cur = sorted[i];
    if (cur === prev + 1) {
      prev = cur;
      continue;
    }
    ranges.push({ type, start: timestamps[start], end: timestamps[prev] });
    start = cur;
    prev = cur;
  }
  ranges.push({ type, start: timestamps[start], end: timestamps[prev] });
  return ranges;
}

export function computeAnomalies(
  anomalyTypesSortedByPriority: AnomalyType[],
  timestamps: Date[],
  data: (number | null)[],
  mean: number,
  sigma: number,
  lcl: number | number[],
  ucl: number | number[],
): AnomalyRange[] {
  if (data.length === 0) return [];
  const anomalyIndexSets = computeAnomalyIndexSets(anomalyTypesSortedByPriority, data, mean, sigma, lcl, ucl);

  const result: AnomalyRange[] = [];
  for (const type of anomalyTypesSortedByPriority) {
    const indices = anomalyIndexSets[type];
    if (indices) {
      result.push(...mergeIndices(indices, type, timestamps));
    }
  }
  return result;
}
